import { useSearchParams, useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  Icon,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Tag,
  TopNav,
  icons,
  theme,
  type BadgeTone,
  type TableColumn,
} from '@agentisai/design-system';
import {
  EVAL_RUN_ID,
  EVAL_STATUSES,
  type EvalCaseResult,
  type EvalRunSummary,
  type EvalStatus,
} from '@agentisai/shared-types';
import { UserMenu } from '../components/UserMenu';
import { EvalDocumentsPanel } from './evals/EvalDocumentsPanel';
import { muted } from './evals/format';
import { EvalSetsPanel } from './evals/EvalSetsPanel';
import { useEvalRun, useEvalRuns } from '../queries/useEvals';
import { useMe } from '../queries/useMe';

/** `?run=` — the open run. URL-owned (R3) so a run is linkable from a PR or chat. */
const RUN_PARAM = 'run';
/** `?tab=` — runs (default) / question sets / documents. URL-owned so a tab is linkable. */
const TAB_PARAM = 'tab';
const TABS = [
  { value: 'runs', label: 'Runs' },
  { value: 'sets', label: 'Question sets' },
  { value: 'documents', label: 'Documents' },
] as const;
type Tab = (typeof TABS)[number]['value'];
const isTab = (v: string | null): v is Tab => TABS.some((t) => t.value === v);

/** Judge criteria, in reading order — `overall` first, the rubric's five, then the case's own. */
const CRITERIA = ['overall', 'grounded', 'correct', 'honest', 'on_task', 'clear', 'case_specific'];
const CRITERION_LABEL: Record<string, string> = {
  overall: 'Overall',
  grounded: 'Grounded',
  correct: 'Correct',
  honest: 'Honest',
  on_task: 'On task',
  clear: 'Clear',
  case_specific: 'Case rubric',
};
const pct = (n: number) => `${Math.round(n * 100)}`;

const STATUS_TONE: Record<EvalStatus, BadgeTone> = {
  fail: 'danger',
  error: 'danger',
  review: 'warn',
  known_gap: 'neutral',
  pass: 'success',
  skipped: 'neutral',
};

const STATUS_LABEL: Record<EvalStatus, string> = {
  fail: 'Fail',
  error: 'Error',
  review: 'Needs review',
  known_gap: 'Known gap',
  pass: 'Pass',
  skipped: 'Skipped',
};

/** Worst first — the page answers "what broke?" before "what passed?". */
const STATUS_ORDER: readonly EvalStatus[] = [
  'fail',
  'error',
  'review',
  'known_gap',
  'pass',
  'skipped',
];

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

function Totals({ totals }: { totals: EvalRunSummary['totals'] }) {
  return (
    <Stack direction="row" gap={1} wrap>
      {EVAL_STATUSES.filter((s) => totals[s] > 0).map((s) => (
        <Badge key={s} tone={STATUS_TONE[s]} size="sm">
          {totals[s]} {STATUS_LABEL[s].toLowerCase()}
        </Badge>
      ))}
    </Stack>
  );
}
Totals.displayName = 'Totals';

const pre = {
  margin: 0,
  whiteSpace: 'pre-wrap' as const,
  fontSize: theme.typography.fontSize.bodySm,
  background: theme.color.neutral.bgSurface,
  padding: theme.space[3],
  borderRadius: theme.radii.md,
  fontFamily: theme.typography.fontFamily.mono,
};

function JudgeStats({ stats }: { stats: NonNullable<EvalCaseResult['judgeStats']> }) {
  const rows = CRITERIA.filter((c) => stats[c]);
  if (rows.length === 0) return null;
  const spread = (stats.overall?.n ?? 0) > 1;
  return (
    <Stack direction="row" gap={2} wrap aria-label="Judge scores">
      {rows.map((c) => {
        const s = stats[c]!;
        return (
          <Badge
            key={c}
            tone={s.mean >= 0.7 ? 'success' : s.mean >= 0.4 ? 'warn' : 'danger'}
            size="sm"
          >
            {CRITERION_LABEL[c] ?? c} {pct(s.mean)}
            {spread && ` ±${pct(s.stdev)}`}
          </Badge>
        );
      })}
    </Stack>
  );
}
JudgeStats.displayName = 'JudgeStats';

function RunColumn({
  run,
  index,
  total,
}: {
  run: EvalCaseResult['runs'][number];
  index: number;
  total: number;
}) {
  const passed = !run.error && run.failures.length === 0;
  const overall = run.judge?.scores.overall;
  return (
    <Stack direction="column" gap={2} style={{ minWidth: 0 }}>
      <Stack direction="row" gap={2} align="center" wrap>
        {total > 1 && <strong>Run {run.rep ?? index + 1}</strong>}
        <Badge tone={passed ? 'success' : 'danger'} size="sm">
          {run.error ? 'Error' : passed ? 'Checks pass' : 'Checks fail'}
        </Badge>
        {overall != null && (
          <Badge tone="neutral" size="sm">
            Judge {pct(overall)}
          </Badge>
        )}
      </Stack>
      {run.judge?.error && <span style={muted}>Judge failed: {run.judge.error}</span>}
      {run.judge?.reasons.overall && <span style={muted}>{run.judge.reasons.overall}</span>}
      {run.transcript?.map((t, i) => (
        <Stack key={i} direction="column" gap={1}>
          <span style={muted}>User</span>
          <p style={{ margin: 0 }}>{t.user}</p>
          <span style={muted}>
            {t.agent}
            {t.modelCalls != null && ` · ${t.modelCalls} model calls`}
          </span>
          <pre style={pre}>{t.reply}</pre>
        </Stack>
      ))}
    </Stack>
  );
}
RunColumn.displayName = 'RunColumn';

function CaseCard({ result }: { result: EvalCaseResult }) {
  const failures = result.runs.flatMap((r, i) =>
    r.failures.map((f) => (result.runs.length > 1 ? `run ${r.rep ?? i + 1}: ${f}` : f)),
  );
  const errors = result.runs.flatMap((r) => (r.error ? [r.error] : []));
  const showRate = result.passRate != null && result.runs.length > 1;
  const withTranscript = result.runs.filter((r) => r.transcript?.length || r.judge);
  return (
    <Card as="section" aria-label={`${result.id} ${result.name}`} elevation={1}>
      <Card.Header>
        <Stack direction="row" gap={2} align="center" wrap>
          <Badge tone={STATUS_TONE[result.status]}>{STATUS_LABEL[result.status]}</Badge>
          <Card.Title>{result.name}</Card.Title>
        </Stack>
        <Card.Meta>
          {[result.id, result.set, result.feature, result.layer].filter(Boolean).join(' · ')}
          {showRate &&
            ` · passed ${Math.round((result.passRate ?? 0) * result.runs.length)} of ${result.runs.length} runs`}
        </Card.Meta>
      </Card.Header>
      <Card.Content>
        <Stack direction="column" gap={3}>
          {(result.tags?.length ?? 0) > 0 && (
            <Stack direction="row" gap={1} wrap>
              {result.tags?.map((t) => (
                <Tag key={t} size="sm">
                  {t}
                </Tag>
              ))}
            </Stack>
          )}
          {result.judgeStats && <JudgeStats stats={result.judgeStats} />}
          {result.reason && <span style={muted}>Skipped: {result.reason}</span>}
          {failures.length > 0 && (
            <ul style={{ margin: 0, paddingInlineStart: theme.space[5] }}>
              {failures.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
          {errors.map((e, i) => (
            <Callout key={i} tone="danger">
              {e}
            </Callout>
          ))}
          {result.judge && (
            <Callout tone="tip">
              <strong>Rubric:</strong> {result.judge}
            </Callout>
          )}
          {withTranscript.length > 0 && (
            // Native <details>: a disclosure with no state to own. Collapsed
            // by default — transcripts are long and read only on demand. With
            // repeats, the runs sit side by side so the drift is visible.
            <details>
              <summary style={{ cursor: 'pointer' }}>
                {withTranscript.length > 1
                  ? `Transcripts (${withTranscript.length} runs, side by side)`
                  : 'Transcript'}
              </summary>
              <div
                style={{
                  marginTop: theme.space[3],
                  display: 'grid',
                  gap: theme.space[4],
                  gridTemplateColumns: `repeat(${withTranscript.length}, minmax(260px, 1fr))`,
                  overflowX: 'auto',
                }}
              >
                {withTranscript.map((run, i) => (
                  <RunColumn
                    key={run.workstreamId || i}
                    run={run}
                    index={i}
                    total={withTranscript.length}
                  />
                ))}
              </div>
            </details>
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
}
CaseCard.displayName = 'CaseCard';

function RunDetail({ runId }: { runId: string }) {
  const query = useEvalRun(runId, true);
  if (query.isPending) return <Skeleton shape="card" height={240} />;
  if (query.isError) {
    return (
      <div role="alert">
        <Callout tone="danger">Couldn't load run {runId}.</Callout>
      </div>
    );
  }
  const run = query.data.data;
  const cases = [...run.cases].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  );
  return (
    <Stack as="section" aria-label={`Run ${run.runId}`} direction="column" gap={4}>
      <Stack direction="column" gap={1}>
        <h2 style={{ margin: 0, fontSize: theme.typography.fontSize.titleLg }}>
          {run.dataset} · {formatWhen(run.startedAt)}
        </h2>
        <span style={muted}>
          {run.target} · {run.model}
          {run.judgeModel && ` · judge ${run.judgeModel}`}
          {run.passScore != null && ` (pass ≥ ${run.passScore})`}
          {(run.repeat ?? 1) > 1 && ` · ${run.repeat}× each case`}
          {(run.tags?.length ?? 0) > 0 && ` · tags ${run.tags?.join(', ')}`} · commit {run.gitSha} ·{' '}
          {run.runId}
        </span>
        <Totals totals={run.totals} />
      </Stack>
      {cases.map((c) => (
        <CaseCard key={c.id} result={c} />
      ))}
    </Stack>
  );
}
RunDetail.displayName = 'RunDetail';

/**
 * EvalsPage (ADR-0072) — staff-only view of agent eval runs published by
 * `apps/workstreamagent/api/evals/run.py --publish`. Hidden unless `/me`
 * grants `evals:read`; the BFF re-checks the grant on every read.
 */
export function EvalsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useMe();
  const permitted = me.data?.data.permissions.includes('evals:read') ?? false;
  const canWrite = me.data?.data.permissions.includes('evals:write') ?? false;
  const rawTab = searchParams.get(TAB_PARAM);
  const tab: Tab = isTab(rawTab) ? rawTab : 'runs';
  const runs = useEvalRuns(permitted && tab === 'runs');
  // Validated at the boundary (R3): a hand-edited ?run= never keys a request.
  const rawRun = searchParams.get(RUN_PARAM);
  const runId = rawRun && EVAL_RUN_ID.test(rawRun) ? rawRun : undefined;

  const columns: TableColumn<EvalRunSummary>[] = [
    { key: 'startedAt', header: 'Started', render: (r) => formatWhen(r.startedAt) },
    { key: 'dataset', header: 'Dataset' },
    { key: 'target', header: 'Target' },
    { key: 'gitSha', header: 'Commit' },
    { key: 'totals', header: 'Result', render: (r) => <Totals totals={r.totals} /> },
    {
      key: 'open',
      header: 'Open',
      ariaLabel: 'Open run',
      render: (r) => (
        <Button
          variant="link"
          size="sm"
          aria-current={r.runId === runId ? 'true' : undefined}
          aria-label={`Open run from ${formatWhen(r.startedAt)}`}
          onClick={() => setSearchParams({ [RUN_PARAM]: r.runId })}
        >
          {r.runId === runId ? 'Viewing' : 'View'}
        </Button>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.color.neutral.bgSurface,
      }}
    >
      <TopNav onBrandClick={() => void navigate('/')} trailing={<UserMenu />} />
      <main style={{ padding: theme.space[6], maxWidth: 960, margin: '0 auto', width: '100%' }}>
        <h1
          style={{
            margin: 0,
            marginBottom: theme.space[2],
            fontSize: theme.typography.fontSize.h2,
            fontWeight: theme.typography.fontWeight.semibold,
          }}
        >
          Agent evals
        </h1>
        <p style={{ ...muted, marginTop: 0, marginBottom: theme.space[5] }}>
          Behaviour evals run against real Bedrock. An AI judge grades every reply against the
          case's rubric and decides pass or fail. “Needs review” only appears for runs made without
          the judge.
        </p>

        {me.isPending && <Skeleton shape="card" height={220} />}

        {me.isError && (
          <div role="alert">
            <Callout tone="danger">
              Couldn't check your access to agent evals. Reload the page to try again.
            </Callout>
          </div>
        )}

        {me.isSuccess && !permitted && (
          <EmptyState
            icon={<Icon as={icons.ShieldCheck} size="lg" />}
            title="You don't have access to agent evals"
            description="This page is for platform staff. Ask an operator to grant evals:read."
          />
        )}

        {permitted && (
          <Tabs
            aria-label="Agent eval sections"
            items={[...TABS]}
            value={tab}
            onChange={(v) => setSearchParams(v === 'runs' ? {} : { [TAB_PARAM]: v })}
            style={{ marginBottom: theme.space[5] }}
          />
        )}
        {permitted && tab === 'sets' && <EvalSetsPanel canWrite={canWrite} />}
        {permitted && tab === 'documents' && <EvalDocumentsPanel canWrite={canWrite} />}

        {permitted && tab === 'runs' && runs.isPending && <Skeleton shape="card" height={220} />}
        {permitted && tab === 'runs' && runs.isError && (
          <div role="alert">
            <Callout tone="danger">Couldn't load eval runs. Reload the page to try again.</Callout>
          </div>
        )}
        {permitted &&
          tab === 'runs' &&
          runs.isSuccess &&
          (runs.data.data.runs.length === 0 ? (
            <EmptyState
              icon={<Icon as={icons.ShieldCheck} size="lg" />}
              title="No eval runs published yet"
              description="Run the suite with --publish from apps/workstreamagent/api to see results here."
            />
          ) : (
            <Stack direction="column" gap={6}>
              <Table
                caption="Recent eval runs"
                rows={runs.data.data.runs}
                columns={columns}
                getRowId={(r) => r.runId}
                density="compact"
              />
              {runId && <RunDetail runId={runId} />}
            </Stack>
          ))}
      </main>
    </div>
  );
}
EvalsPage.displayName = 'EvalsPage';
